/**
 * THE THREE SCIENTIFIC READING SURFACES — OBSERVATORY, EVIDENCE, KNOWLEDGE.
 *
 * WHY THEY ARE ONE MODULE. They are three readings of ONE record set, and what
 * must not drift between them is exactly what they share: the vocabulary a
 * finding's bearing is rendered in, the rule that an external claim is never
 * canonical truth, the way a bounded list states its own bound, and the chips
 * that carry a record into the Main Chat. Three files would be three copies of
 * "this is an external claim, not system truth", and a copy is a place for the
 * sentence to change without the others noticing.
 *
 * WHAT THEY ARE NOT. None is an activity feed. Every line is a stored record
 * with a provenance, and where the product cannot say something the surface says
 * THAT rather than filling the space.
 *
 * ---------------------------------------------------------------------------
 * THE FOUR RULES
 * ---------------------------------------------------------------------------
 *
 *   1. A BOUNDED LIST STATES ITS BOUND. Every list route here returns a page —
 *      forty episodes, fifty hypotheses — so a view that rendered "the
 *      episodes" without saying so would show an arbitrary page while reading
 *      as the whole corpus. Each list says what it asked for and that it is a
 *      page; a full page is followed by the view's own continuation control,
 *      and the ONE place corpus truth exists (`sourcesTotal`/`findingsTotal` on
 *      an episode) is rendered BESIDE the page rather than instead of it.
 *
 *   2. AN EXTERNAL CLAIM IS NEVER SYSTEM TRUTH. Findings carry
 *      `externalClaim: true` by construction. The episode's own `provenanceNote`
 *      is rendered verbatim rather than paraphrased.
 *
 *   3. NEGATIVE AND INCONCLUSIVE WORK IS FIRST-CLASS. `unresolved` — the
 *      unanswered question, the oppositions left standing, the findings that
 *      gave only context — is rendered as prominently as the findings, because a
 *      view showing only what was found would make a window that established
 *      nothing look EMPTY rather than INCONCLUSIVE.
 *
 *   4. NOTHING HERE RUNS A MODEL. Every read is a GET, so opening a page cannot
 *      spend.
 */

import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import { createLoadable, expectsMembers } from '../resource';
import { readGateway, type GatewayError } from '../gateway';
import { Failure } from './failure';
import { DiscussLink } from './discuss';
import { route } from '../router';
import { PageHeader, Technical } from './about';
import { Markdown } from '../markdown';
import { formatTurnTime as formatRecordTime } from '../chat';

/**
 * THE PATH SEGMENTS THAT NAME ONE RECORD IN THIS FILE'S THREE VIEWS.
 *
 * `/observatory/episode/<episodeId>` opens an investigation — and is where a
 * search result for a FINDING or a SOURCE lands, because the episode's body is
 * the surface that renders them with the sources they cite.
 *
 * `/evidence/record/<ref>` opens whatever canonical record a reference names,
 * through the same resolver `/gateway/evidence/ref` already serves and this
 * view already renders. It is the workspace's record reader: it answers a
 * record's status, its summary and its document, and it reports when a record
 * has moved since the reference was minted.
 *
 * `/evidence/adjudication/<id>` opens one adjudication in the list below it,
 * when that adjudication is inside the page this read returns.
 */
export const EPISODE_SECTION = 'episode';
export const RECORD_SECTION = 'record';
export const ADJUDICATION_SECTION = 'adjudication';

/* --------------------------------------------------------------- the shapes */

interface Disclosure {
	version: string;
	generatedAt: string;
	authority: string;
}

interface EpisodeSummary {
	episodeId: string;
	question: string;
	status: string;
	gapKind: string;
	runId: string | null;
	parentEpisodeId: string | null;
	createdAt: string;
	updatedAt: string;
	convergenceReason: string | null;
}

interface EpisodeDetail extends EpisodeSummary {
	target: { targetType: string; targetId: string; environment: string };
	deadBatches: number;
	seenLocators: string[];
	sourcesTotal: number;
	findingsTotal: number;
	sources: Array<{
		sourceId: string;
		locator: string;
		title: string;
		quality: string;
		claimSummary: string;
		publishedAt: string | null;
		retrievedAt: string;
		qualityBasis: string | null;
		searchProvider: string | null;
		searchRank: number | null;
	}>;
	findings: Array<{
		findingId: string;
		claim: string;
		sourceIds: string[];
		bearsOn: string;
		bearingBasis: string | null;
		confidence: string;
		recordedAt: string;
	}>;
	unresolved: {
		question: string;
		findingsBearingOnQuestion: number;
		findingsContextOnly: number;
		unanswered: boolean;
		oppositionsUnresolved: string[];
		reason: string;
	} | null;
}

interface EpisodeDetailPayload extends Disclosure {
	found: boolean;
	episodeId: string;
	episode?: EpisodeDetail;
	resources?: {
		sourcesRead: number;
		sourcesTotal: number;
		sourcesCoveredByAdjudication: number;
		sourcesShownByAdjudication: number;
		adjudications: number;
	};
	provenanceNote?: string;
}

interface Hypothesis {
	hypothesisId: string;
	statement: string;
	status: string;
	confidence: string;
	discriminators: string[];
	seeking: unknown;
	supersededByHypothesisId: string | null;
	convergenceReason: string | null;
	createdAt: string;
	lastRevisedAt: string | null;
}

interface Adjudication {
	adjudicationId: string;
	episodeId: string;
	/** THE CLAIM THE STANDARD WEIGHED. The gateway's record names it `claim`;
	 *  this view used to read `proposition`, which no producer sends — so the
	 *  real Evidence Explorer rendered every adjudication with an EMPTY
	 *  summary while the render specimen, built from the same invented field,
	 *  passed. The field and the assertion now use the record's own name. */
	claim: string;
	question: string;
	verdict: string;
	standard: string;
	decidedAt: string;
	links: Array<{
		sourceId: string;
		locator: string;
		excerpt: string;
		publisher: string | null;
		bearsOn: string;
	}>;
	discarded: Array<{ locator: string; reason: string }>;
}

interface ReferenceAnswer {
	ref: string;
	status: string;
	kind: string | null;
	id: string | null;
	summary: string;
	reason: string;
	/** THE RECORD'S OWN TEXT, AS A STRING, BECAUSE THAT IS WHAT THE RESOLVER
	 *  RETURNS. This field was declared `{ text, truncated }` and the service
	 *  sends a plain string, so `document.text` was `undefined` and a resolved
	 *  document rendered as an empty block — a record that was FOUND presented
	 *  as a record with nothing in it. The cut is not a separate flag on the
	 *  wire: the resolver states it inside the text it returns (see
	 *  `boundDocument`), which is why the view below renders the string whole
	 *  and adds no claim of its own about where it ends. */
	document?: string;
}

interface TheoryPayload extends Disclosure {
	found: boolean;
	targetKey: string;
	theory?: {
		revision: number;
		components: unknown;
		processes: unknown;
		knownUnknowns: unknown;
		lastRevisedAt: string | null;
		updatedAt: string;
	};
	beliefs?: Array<{
		beliefKey: string;
		statement: string;
		confidence: string;
		status: string;
		supersededByBeliefKey: string | null;
		createdAt: string;
		lastRevisedAt: string | null;
	}>;
	knownUnknowns?: Array<{
		unknownKey: string;
		statement: string;
		revealedByRef: string | null;
		recordedAt: string;
		resolvedAt: string | null;
		resolvedByRef: string | null;
	}>;
}

/**
 * ONE DURABLE KNOWLEDGE ENTRY — Scout's own explanation of something it learned,
 * with its scientific status and the primary evidence it cites.
 *
 * authorship is stored, not inferred: every entry this view renders was written
 * by Scout. The Harness supplies the evidence and decides whether a proposed
 * status may stand; it does not write the entry.
 */
interface LibraryEntry {
	entryId: string;
	runId: string | null;
	episodeId: string | null;
	topicId: string | null;
	targetKey: string | null;
	title: string;
	learned: string;
	whyItMatters: string;
	limitations: string;
	knowledgeStatus:
		| 'SUPPORTED'
		| 'PROVISIONAL'
		| 'DISPUTED'
		| 'REFUTED'
		| 'SUPERSEDED'
		| 'UNRESOLVED';
	statusNote: string | null;
	evidenceRefs: string[];
	workerReportRefs: string[];
	relatedEntryIds: string[];
	supersedesEntryId: string | null;
	supersededByEntryId: string | null;
	revision: number;
	createdAt: string;
}

interface LibraryPayload extends Disclosure {
	wired: boolean;
	reason?: string;
	entries: LibraryEntry[];
	/** THE PAGE'S OWN CONTINUATION (H-035) — read from the route, not assumed. */
	offset?: number;
	limit?: number;
	hasMore?: boolean;
}

interface WeeklyPayload extends Disclosure {
	weekly: {
		material: boolean;
		summary: string;
		windowStart: string;
		windowEnd: string;
		falsifiedBeliefs?: Array<{ beliefKey: string; statement: string; reason: string }>;
		revisions?: Array<{ beliefKey: string; changeKind: string; reason: string; changedAt: string }>;
	};
}

/** HOW MANY EACH LIST ASKS FOR. Named here rather than left to the server's
 *  default, because the view has to SAY how many it asked for — a bound a view
 *  cannot state is a bound it cannot be honest about. */
const PAGE = { episodes: 40, hypotheses: 50, adjudications: 50, knowledge: 50 } as const;

/* --------------------------------------------------------------- the pieces */

/** THE BOUND, STATED. Every list in this module renders one of these, because
 *  every list in this product caps silently and a page read as a corpus is a
 *  claim about completeness that nothing supports. */
function Bounded({ shown, of, noun }: { shown: number; of?: number; noun: string }) {
	return (
		<p class="countline small">
			Showing <span class="count">{shown}</span> {noun}
			{of === undefined ? (
				<span class="muted">
					{' '}
					— the newest this read asked for. A full page is followed by the view's own Load more
					control where the route provides a continuation; the count is this page's, not the
					corpus's.
				</span>
			) : (
				<span class="muted">
					{' '}
					of <span class="count">{of}</span> the record holds
				</span>
			)}
		</p>
	);
}

/** A FINDING'S BEARING, IN THE RECORD'S OWN WORDS. `bearingBasis` says what the
 *  bearing was decided FROM, and `ESTABLISHED` is a basis nothing in this
 *  release writes — so it is rendered as absent rather than implied. */
const BEARING_CAPTIONS: Record<string, string> = {
	SUPPORTS: 'the evidence bears on the question in favour',
	REFUTES: 'the evidence bears against it',
	CONTEXT: 'the evidence is context only — it does not bear either way',
};

const BASIS_CAPTIONS: Record<string, string> = {
	PAGE_EXCERPT: 'decided from a verbatim span of the page',
	TERM_OVERLAP: 'decided from shared terms, NOT from a span — the weakest basis the record carries',
	ESTABLISHED: 'recorded as established',
};

/** A BEARING, IN THE RECORD'S OWN WORDS. The basis and the confidence are
 *  OPTIONAL because an adjudicated link carries a bearing without either — and a
 *  component that demanded them would force the caller to invent them, which is
 *  how a screen comes to state a confidence nothing recorded. */
function Bearing({
	bearsOn,
	basis,
	confidence,
}: {
	bearsOn: string;
	basis?: string | null;
	confidence?: string | null;
}) {
	return (
		<p class="small">
			<code>{bearsOn}</code>{' '}
			<span class="muted">
				— {BEARING_CAPTIONS[bearsOn] ?? 'this build does not describe this bearing'}
			</span>
			{basis === undefined ? null : basis === null ? (
				<span class="muted"> · the record does not say what this was decided from</span>
			) : (
				<span class="muted"> · {BASIS_CAPTIONS[basis] ?? basis}</span>
			)}
			{confidence !== undefined && confidence !== null && (
				<span class="muted"> · confidence {confidence.toLowerCase()}</span>
			)}
		</p>
	);
}

/** A SOURCE, AS THE RECORD HOLDS IT. `locator` is the URL precisely enough to
 *  return to; `claimSummary` is SCOUT'S BOUNDED READING of it, never a copy of
 *  the page — and the two are labelled so nobody reads the second as the first. */
function Source({ source }: { source: EpisodeDetail['sources'][number] }) {
	return (
		<li class="record">
			<div class="record-head">
				<strong>{source.title}</strong>
				<span class="tag">{source.quality}</span>
			</div>
			<p class="pointer small">
				<code>{source.locator}</code>
			</p>
			<p class="record-summary small">
				<span class="muted">Scout’s own reading of it, not a copy of the page:</span>{' '}
				{source.claimSummary}
			</p>
			<p class="muted small">
				read <time dateTime={source.retrievedAt}>{source.retrievedAt}</time>
				{source.publishedAt !== null && (
					<>
						{' '}
						· published <time dateTime={source.publishedAt}>{source.publishedAt}</time>
					</>
				)}
				{source.searchProvider !== null && <> · found via {source.searchProvider}</>}
				{source.searchRank !== null && <> at rank {source.searchRank}</>}
			</p>
			{source.qualityBasis !== null && (
				<p class="muted small">quality basis: {source.qualityBasis}</p>
			)}
			<p>
				<DiscussLink ref={`research:${source.sourceId}`} label="Discuss this source" />
			</p>
		</li>
	);
}

/* ------------------------------------------------------------ the Observatory */

export function ObservatoryView() {
	const [episodesOlder, setEpisodesOlder] = useState<EpisodeSummary[]>([]);
	const [episodesMore, setEpisodesMore] = useState(false);
	const [episodesPaging, setEpisodesPaging] = useState(false);
	const [episodesPageError, setEpisodesPageError] = useState<GatewayError | null>(null);
	const episodes = useMemo(
		() =>
			createLoadable<{ episodes: EpisodeSummary[]; hasMore?: boolean }>(
				() => readGateway(`/api/scout/gateway/research/episodes?limit=${PAGE.episodes}`),
				expectsMembers('episodes'),
			),
		[],
	);
	const [openId, setOpenId] = useState<string | null>(null);
	const detail = useMemo(
		() =>
			createLoadable<EpisodeDetailPayload>(() =>
				openId === null
					? Promise.resolve({ ok: true as const, data: { found: false, episodeId: '' } as never })
					: readGateway<EpisodeDetailPayload>(
							`/api/scout/gateway/research/${encodeURIComponent(openId)}`,
						),
				// 'found' IS THE MEMBER THE VIEW BRANCHES ON, and it is the one
				// the sentinel above supplies when nothing is open. A payload
				// without it is not an answer to "is this episode there".
				expectsMembers('found'),
			),
		[openId],
	);

	useEffect(() => {
		setEpisodesOlder([]);
		setEpisodesMore(false);
		setEpisodesPageError(null);
		void episodes.load();
	}, [episodes]);
	useEffect(() => {
		if (openId !== null) void detail.load();
	}, [detail, openId]);

	// A DEEP LINK OPENS THE INVESTIGATION IT NAMES — `/observatory/episode/<id>`.
	//
	// THIS IS WHERE A SEARCH RESULT FOR A FINDING OR A SOURCE LANDS, and it is
	// where those records are rendered: the episode's body is the only surface
	// that shows an investigation's findings with the sources they cite, so a
	// link to the episode opens the record rather than a copy of it. The episode
	// list below may not contain the id at all — search reaches older episodes
	// than one page of this list holds — and that is exactly why the open is
	// driven by the route and the detail read rather than by the list.
	useEffect(() => {
		if (route.value.destination !== 'research' || route.value.section !== 'observatory') {
			return;
		}
		const linked = route.value.recordId;
		if (linked === null || linked === openId) return;
		setOpenId(linked);
	}, [route.value.section, route.value.recordId, openId]);

	const listState = episodes.state.value;
	const detailState = detail.state.value;
	const listRows = [...(listState.data?.episodes ?? []), ...episodesOlder];
	const listHasMore =
		episodesMore || (episodesOlder.length === 0 && listState.data?.hasMore === true);
	const loadMoreEpisodes = useCallback(async () => {
		if (listState.status !== 'ready' || listState.data === null) return;
		setEpisodesPaging(true);
		setEpisodesPageError(null);
		const offset = listState.data.episodes.length + episodesOlder.length;
		const result = await readGateway<{ episodes: EpisodeSummary[]; hasMore?: boolean }>(
			`/api/scout/gateway/research/episodes?limit=${PAGE.episodes}&offset=${offset}`,
		);
		setEpisodesPaging(false);
		if (!result.ok) {
			setEpisodesPageError(result.error);
			return;
		}
		setEpisodesOlder((previous) => [...previous, ...result.data.episodes]);
		setEpisodesMore(result.data.hasMore === true);
	}, [listState, episodesOlder]);

	return (
		<section class="view" aria-labelledby="observatory-heading">
			<PageHeader
				id="observatory-heading"
				title="Observatory"
				about={{
					purpose:
						'What Scout investigated, what it read, what it concluded — and what it could not settle.',
					how: (
						<p>
							Open an investigation to see the sources it read and the findings it recorded, with the
							quality class of each source and the basis for each finding’s bearing. A finding that
							bears on the question and one that is only context are deliberately different.
						</p>
					),
					limits: (
						<>
							<p>
								<strong>There is no segment-by-segment history.</strong> The product records how
								many segments ran and what each investigation produced; it does not store a readable
								record of what a past segment did.
							</p>
							<p>
								<strong>There are no retrieval events beyond the source row.</strong> A source
								carries when it was read, by which provider and at what rank — that is all.
							</p>
							<p>
								<strong>The list is a page, not the corpus.</strong> No cursor and no total exist for
								investigations, so this view cannot tell you how many there are.
							</p>
						</>
					),
				}}
			/>

			<section class="panel" aria-labelledby="episodes-heading">
				<h2 id="episodes-heading">Investigations</h2>
				{listState.status === 'loading' && (
					<p class="notice" role="status">
						Reading Scout’s investigations…
					</p>
				)}
				{listState.status === 'failed' && listState.error !== null && (
					<Failure error={listState.error} what="the investigations" onRetry={() => void episodes.load()} />
				)}
				{listState.status === 'ready' && listState.data !== null && (
					<>
						{listRows.length === 0 ? (
							<p>
								<strong>No investigation is on record.</strong>{' '}
								<span class="muted">
									An investigation is opened when Scout looks into a question — by the Owner asking,
									or by a window choosing one. An empty list here means none has been opened, not
									that one is hidden.
								</span>
							</p>
						) : (
							<>
								<Bounded shown={listRows.length} noun="investigation(s)" />
								<ul class="records">
									{listRows.map((episode) => (
										<li key={episode.episodeId} class="record">
											<div class="record-head">
												<code>{episode.episodeId}</code>
												<span class="tag">{episode.status}</span>
												{episode.runId !== null && <span class="tag">{episode.runId}</span>}
											</div>
											<p class="record-summary">{episode.question}</p>
											<p class="muted small">
												opened <time dateTime={episode.createdAt}>{episode.createdAt}</time>
												{episode.parentEpisodeId !== null && (
													<>
														{' '}
														· branched from <code>{episode.parentEpisodeId}</code>
													</>
												)}
												{' '}
												· gap kind <code>{episode.gapKind}</code>
											</p>
											<p>
												<button
													type="button"
													class="button ghost small"
													aria-expanded={openId === episode.episodeId}
													onClick={() =>
														setOpenId(openId === episode.episodeId ? null : episode.episodeId)
													}
												>
													{openId === episode.episodeId ? 'Close' : 'Open the record'}
												</button>{' '}
												<DiscussLink ref={`research:${episode.episodeId}`} label="Discuss" />
											</p>
										</li>
									))}
								</ul>
								{listHasMore && (
									<button
										type="button"
										class="button ghost"
										disabled={episodesPaging}
										onClick={() => void loadMoreEpisodes()}
									>
										{episodesPaging ? 'Reading the next page…' : 'Load more investigations'}
									</button>
								)}
								{episodesPageError !== null && (
									<p class="notice small" role="status">
										The next page of investigations could not be read:{' '}
										{episodesPageError.message}
									</p>
								)}
							</>
						)}
					</>
				)}
			</section>

			{openId !== null && (
				<section class="panel" aria-labelledby="detail-heading">
					<h2 id="detail-heading">The record</h2>
					{detailState.status === 'loading' && (
						<p class="notice" role="status">
							Reading this investigation…
						</p>
					)}
					{detailState.status === 'failed' && detailState.error !== null && (
						<Failure error={detailState.error} what="this investigation" onRetry={() => void detail.load()} />
					)}
					{detailState.status === 'ready' && detailState.data !== null && (
						<EpisodeBody payload={detailState.data} />
					)}
				</section>
			)}
		</section>
	);
}

function EpisodeBody({ payload }: { payload: EpisodeDetailPayload }) {
	if (!payload.found || payload.episode === undefined) {
		return (
			<p class="notice">
				<strong>No such investigation for this owner.</strong> The read returned nothing for{' '}
				<code>{payload.episodeId}</code> — which is what a record belonging to nobody, or to somebody
				else, answers with.
			</p>
		);
	}
	const episode = payload.episode;
	return (
		<>
			<p class="verdict">{episode.question}</p>
			<p class="muted small">
				<code>{episode.episodeId}</code> · {episode.status}
				{episode.convergenceReason !== null && <> · {episode.convergenceReason}</>}
			</p>
			{/* THE PROVENANCE NOTE IS THE RECORD'S OWN SENTENCE about what a finding
			    is. Rendered verbatim rather than restated, because a restatement is
			    a place for it to soften. */}
			{payload.provenanceNote !== undefined && <p class="authority">{payload.provenanceNote}</p>}

			{payload.resources !== undefined && (
				<p class="muted small">
					{payload.resources.sourcesRead} source(s) in this page of {payload.resources.sourcesTotal}{' '}
					read · {payload.resources.adjudications} adjudication(s) ·{' '}
					{payload.resources.sourcesCoveredByAdjudication} source(s) judged
					{payload.resources.sourcesShownByAdjudication !==
						payload.resources.sourcesCoveredByAdjudication && (
						<>
							{' '}
							({payload.resources.sourcesShownByAdjudication} shown to the judge, which may exceed
							the number judged)
						</>
					)}
				</p>
			)}

			<h3 class="subhead">What was read</h3>
			{episode.sources.length === 0 ? (
				<p class="muted small">
					<strong>No source is on record for this investigation.</strong> Nothing was read, or nothing
					read was kept — and this view does not guess which.
				</p>
			) : (
				<>
					<Bounded shown={episode.sources.length} of={episode.sourcesTotal} noun="source(s)" />
					<ul class="records">
						{episode.sources.map((source) => (
							<Source key={source.sourceId} source={source} />
						))}
					</ul>
				</>
			)}

			<h3 class="subhead">What it concluded</h3>
			{episode.findings.length === 0 ? (
				<p class="muted small">
					<strong>No finding is on record.</strong> An investigation that read sources and recorded no
					finding is a result — it is shown here as what it is rather than as an absence.
				</p>
			) : (
				<>
					<Bounded shown={episode.findings.length} of={episode.findingsTotal} noun="finding(s)" />
					<ul class="records">
						{episode.findings.map((finding) => (
							<li key={finding.findingId} class="record">
								<div class="record-head">
									<code>{finding.findingId}</code>
									<span class="tag tag-warn">external claim</span>
								</div>
								<p class="record-summary">{finding.claim}</p>
								<Bearing
									bearsOn={finding.bearsOn}
									basis={finding.bearingBasis}
									confidence={finding.confidence}
								/>
								<p class="muted small">
									from {finding.sourceIds.length} source(s) · recorded{' '}
									<time dateTime={finding.recordedAt}>{finding.recordedAt}</time>
								</p>
								<p>
									<DiscussLink
										ref={`research:${episode.episodeId}`}
										label="Discuss this finding"
									/>
								</p>
							</li>
						))}
					</ul>
				</>
			)}

			{/* NEGATIVE AND INCONCLUSIVE WORK, AS PROMINENT AS THE FINDINGS. */}
			<h3 class="subhead">What it could not settle</h3>
			{episode.unresolved === null ? (
				<p class="muted small">
					This investigation recorded no unresolved statement. That is not the same as having settled
					everything — the record says what it says, and this view does not add to it.
				</p>
			) : (
				<>
					<p>
						<strong>{episode.unresolved.unanswered ? 'Unanswered.' : 'Settled.'}</strong>{' '}
						{episode.unresolved.reason}
					</p>
					<p class="muted small">
						{episode.unresolved.findingsBearingOnQuestion} finding(s) bore on the question ·{' '}
						{episode.unresolved.findingsContextOnly} gave context only
					</p>
					{episode.unresolved.oppositionsUnresolved.length > 0 && (
						<>
							<p class="small">
								<strong>Oppositions still standing:</strong>
							</p>
							<ul class="records">
								{episode.unresolved.oppositionsUnresolved.map((opposition) => (
									<li key={opposition} class="record small">
										{opposition}
									</li>
								))}
							</ul>
						</>
					)}
				</>
			)}
		</>
	);
}

/* ------------------------------------------------------- the Evidence Explorer */

export function EvidenceView() {
	const [ref, setRef] = useState('');
	const [query, setQuery] = useState<string | null>(null);
	const answer = useMemo(
		() =>
			createLoadable<ReferenceAnswer>(
				() => query === null
					? Promise.resolve({ ok: true as const, data: null as never })
					: readGateway<ReferenceAnswer>(
							`/api/scout/gateway/evidence/ref?ref=${encodeURIComponent(query)}`,
						),
				// THE RESOLVER'S OWN STATUS IS THE MEMBER. Every non-answer the
				// resolver can give — NOT_FOUND, UNREADABLE, MALFORMED — arrives
				// AS a status, so a payload without one is not a resolution at
				// all and must not be rendered as though it were.
				expectsMembers('status', 'ref', 'kind'),
			),
		[query],
	);
	const [adjudicationsOlder, setAdjudicationsOlder] = useState<Adjudication[]>([]);
	const [adjudicationsMore, setAdjudicationsMore] = useState(false);
	const [adjudicationsPaging, setAdjudicationsPaging] = useState(false);
	const [adjudicationsPageError, setAdjudicationsPageError] = useState<GatewayError | null>(null);
	const adjudications = useMemo(
		() =>
			createLoadable<{ adjudications: Adjudication[]; hasMore?: boolean }>(
				() => readGateway(`/api/scout/gateway/adjudications?limit=${PAGE.adjudications}`),
				expectsMembers('adjudications'),
			),
		[],
	);

	useEffect(() => {
		if (query !== null) void answer.load();
	}, [answer, query]);
	useEffect(() => {
		setAdjudicationsOlder([]);
		setAdjudicationsMore(false);
		void adjudications.load();
	}, [adjudications]);

	// A DEEP LINK RESOLVES THE REFERENCE IT NAMES — `/evidence/record/<ref>`.
	//
	// THIS IS THE WORKSPACE'S RECORD READER, and search routes hypotheses and
	// lessons here because it is the only surface that answers "what IS this
	// record, right now" for a reference: the resolver reads canonical state,
	// reports drift since the reference was minted, and refuses a record it
	// cannot read rather than rendering an empty one. The box below is seeded
	// with what arrived so the Owner can see the reference that was resolved and
	// edit it, rather than being shown an answer to an invisible question.
	useEffect(() => {
		if (route.value.destination !== 'discoveries' || route.value.section !== 'evidence') return;
		const linked = route.value.recordId;
		if (linked === null) return;
		// AN ADJUDICATION IS A RECORD OF ITS OWN, and its id says so. A link to
		// one marks the row in the list below; any other reference goes through
		// the resolver, which is what "open this record" means here.
		if (linked.startsWith('adj:')) return;
		setRef(linked);
		setQuery(linked);
	}, [route.value.section, route.value.recordId]);

	// WHICH ADJUDICATION A LINK ASKED FOR. The list below is paged, so a link
	// can still name a record the first page does not hold; the panel says
	// exactly that rather than rendering an unmarked list, and Load more reaches
	// the record without a second route (H-049).
	const anchoredAdjudication =
		route.value.destination === 'discoveries' &&
		route.value.section === 'evidence' &&
		route.value.recordId !== null &&
		route.value.recordId.startsWith('adj:')
			? route.value.recordId
			: null;

	const answerState = answer.state.value;
	const adjudicationState = adjudications.state.value;
	const adjudicationRows = [
		...(adjudicationState.data?.adjudications ?? []),
		...adjudicationsOlder,
	];
	const adjudicationsHasMore =
		adjudicationsMore ||
		(adjudicationsOlder.length === 0 && adjudicationState.data?.hasMore === true);
	const loadMoreAdjudications = useCallback(async () => {
		if (adjudicationState.status !== 'ready' || adjudicationState.data === null) return;
		setAdjudicationsPaging(true);
		setAdjudicationsPageError(null);
		const offset = adjudicationState.data.adjudications.length + adjudicationsOlder.length;
		const result = await readGateway<{ adjudications: Adjudication[]; hasMore?: boolean }>(
			`/api/scout/gateway/adjudications?limit=${PAGE.adjudications}&offset=${offset}`,
		);
		setAdjudicationsPaging(false);
		if (!result.ok) {
			setAdjudicationsPageError(result.error);
			return;
		}
		setAdjudicationsOlder((previous) => [...previous, ...result.data.adjudications]);
		setAdjudicationsMore(result.data.hasMore === true);
	}, [adjudicationState, adjudicationsOlder]);

	return (
		<section class="view" aria-labelledby="evidence-heading">
			<PageHeader
				id="evidence-heading"
				title="Evidence"
				about={{
					purpose:
						'A claim, the citation that names it, and what the record says that citation resolves to.',
					how: (
						<>
							<p>
								Paste any reference the product has written — a finding, a source, a hypothesis, an
								experiment, a window or its report. The same resolver answers here as answers a
								citation in the Main Chat, so a reference cannot mean one thing in one place and
								another elsewhere.
							</p>
							<p>
								The adjudications below say how a claim was allowed to be established, including the
								links that were discarded and the evidence that was not enough.
							</p>
						</>
					),
					limits: (
						<>
							<p>
								<strong>A reference that resolves is not a claim that is true.</strong> Resolution
								says the record exists and is this owner’s; whether what it says is right is what the
								adjudication and the findings are for, and neither is a proof.
							</p>
							<p>
								<strong>A source’s text is not stored.</strong> This product keeps a locator, a
								quality label and Scout’s own bounded reading of a page. The only verbatim text
								anywhere is the span on an adjudicated link.
							</p>
							<p>
								<strong>A source that has since become unreachable still resolves.</strong> Storage
								and retrieval are different things.
							</p>
						</>
					),
				}}
			/>

			<section class="panel" aria-labelledby="resolve-heading">
				<h2 id="resolve-heading">Resolve a reference</h2>
				<p class="muted small">
					Paste any reference the product has written — a finding, a source, a hypothesis, an
					experiment, a window or its report. The SAME resolver answers here as answers a citation in
					the Main Chat, so a reference cannot mean one thing in one place and another elsewhere.
				</p>
				<form
					class="inline-form"
					onSubmit={(event: Event) => {
						event.preventDefault();
						setQuery(ref.trim().length === 0 ? null : ref.trim());
					}}
				>
					<input
						type="text"
						class="composer-input"
						aria-label="A record reference"
						placeholder="research:ep-… · hyp:… · exp-… · run:…:report"
						value={ref}
						onInput={(event: Event) => setRef((event.target as HTMLInputElement).value)}
					/>{' '}
					<button type="submit" class="button">
						Resolve
					</button>
				</form>
				{answerState.status === 'loading' && (
					<p class="notice" role="status">
						Resolving…
					</p>
				)}
				{answerState.status === 'failed' && answerState.error !== null && (
					<Failure error={answerState.error} what="that reference" onRetry={() => void answer.load()} />
				)}
				{answerState.status === 'ready' && answerState.data !== null && (
					<ReferenceCard answer={answerState.data} />
				)}
			</section>

			<section class="panel" aria-labelledby="adjudications-heading">
				<h2 id="adjudications-heading">How a claim was allowed to be established</h2>
				<p class="muted small">
					An adjudication is the decision itself: the standard applied, the links that survived with
					their verbatim spans, and the links that were <strong>discarded with the reason</strong> —
					which is the half a summary would leave out.
				</p>
				{adjudicationState.status === 'loading' && (
					<p class="notice" role="status">
						Reading adjudications…
					</p>
				)}
				{adjudicationState.status === 'failed' && adjudicationState.error !== null && (
					<Failure
						error={adjudicationState.error}
						what="the adjudications"
						onRetry={() => void adjudications.load()}
					/>
				)}
				{adjudicationState.status === 'ready' && adjudicationState.data !== null && (
					<>
						{adjudicationRows.length === 0 ? (
							<p>
								<strong>No adjudication is on record.</strong>{' '}
								<span class="muted">
									An adjudication happens when an investigation weighs its links against a
									standard. None having happened is a fact about the work, not about this page.
								</span>
							</p>
						) : (
							<>
								<Bounded
									shown={adjudicationRows.length}
									noun="adjudication(s)"
								/>
								{/* THE ROW A SEARCH LINKED TO, OR THE REASON IT IS NOT IN
								    THE PAGES LOADED SO FAR. The list now continues with
								    Load more (H-049), so "not here yet" and "does not
								    exist" are different statements — this panel says which. */}
								{anchoredAdjudication !== null && (
									<p class="notice small">
										{adjudicationRows.some(
											(entry) => entry.adjudicationId === anchoredAdjudication,
										) ? (
											<>
												<strong>Opened from a search.</strong>{' '}
												<span class="muted">
													<code>{anchoredAdjudication}</code> is marked below.
												</span>
											</>
										) : (
											<>
												<strong>
													<code>{anchoredAdjudication}</code> is not in the{' '}
													{adjudicationRows.length} adjudications loaded so far.
												</strong>{' '}
												<span class="muted">
													Load more reaches older pages; the id above is the
													one the search found, and the search result carries
													the record’s own words.
												</span>
											</>
										)}
									</p>
								)}
								<ul class="records">
									{adjudicationRows.map((entry) => (
										<li
											key={entry.adjudicationId}
											class={`record${entry.adjudicationId === anchoredAdjudication ? ' is-anchored' : ''}`}
											aria-current={
												entry.adjudicationId === anchoredAdjudication
													? ('true' as const)
													: undefined
											}
										>
											<div class="record-head">
												<code>{entry.adjudicationId}</code>
												<span class="tag">{entry.verdict}</span>
											</div>
											<p class="record-summary">{entry.claim}</p>
											<p class="muted small">
												question: {entry.question}
											</p>
											<p class="muted small">
												standard: {entry.standard} · decided{' '}
												<time dateTime={entry.decidedAt}>{entry.decidedAt}</time>
											</p>
											<h4 class="subhead">Links that survived ({entry.links.length})</h4>
											<ul class="records">
												{entry.links.map((link) => (
													<li key={`${entry.adjudicationId}:${link.sourceId}`} class="record small">
														<p class="pointer">
															<code>{link.locator}</code>
															{link.publisher !== null && <> · {link.publisher}</>}
														</p>
														{/* THE VERBATIM SPAN — the only place in the
														    product a source's own words are kept. Quoted,
														    never restyled, so nobody reads it as Scout's. */}
														<blockquote class="report">{link.excerpt}</blockquote>
														<Bearing bearsOn={link.bearsOn} />
													</li>
												))}
											</ul>
											{entry.discarded.length > 0 && (
												<>
													<h4 class="subhead">
														Links discarded ({entry.discarded.length}) — and why
													</h4>
													<ul class="records">
														{entry.discarded.map((discard) => (
															<li key={`${entry.adjudicationId}:d:${discard.locator}`} class="record small">
																<p class="pointer">
																	<code>{discard.locator}</code>
																</p>
																<p>{discard.reason}</p>
															</li>
														))}
													</ul>
												</>
											)}
										</li>
									))}
								</ul>
								{adjudicationsHasMore && (
									<button
										type="button"
										class="button ghost"
										disabled={adjudicationsPaging}
										onClick={() => void loadMoreAdjudications()}
									>
										{adjudicationsPaging ? 'Reading the next page…' : 'Load more adjudications'}
									</button>
								)}
								{adjudicationsPageError !== null && (
									<p class="notice small" role="status">
										The next page of adjudications could not be read: {adjudicationsPageError.message}
									</p>
								)}
							</>
						)}
					</>
				)}
			</section>
		</section>
	);
}

function ReferenceCard({ answer }: { answer: ReferenceAnswer }) {
	return (
		<div class={`panel ${answer.status === 'RESOLVED' ? '' : 'not-known'}`}>
			<div class="record-head">
				<span class="record-title">Reference</span>
				<span class={`tag${answer.status === 'RESOLVED' ? ' tag-live' : ' tag-warn'}`}>
					{answer.status}
				</span>
				{answer.kind !== null && <span class="tag">{answer.kind}</span>}
			</div>
			<Technical summary="Reference identifier">
				<code>{answer.ref}</code>
			</Technical>
			<p>{answer.reason}</p>
			{answer.status === 'RESOLVED' && answer.summary.length > 0 && (
				<p class="record-summary">{answer.summary}</p>
			)}
			{typeof answer.document === 'string' && answer.document.length > 0 && (
				<>
					<blockquote class="report">{answer.document}</blockquote>
					<p class="muted small">
						This is the record’s own stored text, returned by the resolver and shown
						unrestyled. Where a record is longer than one answer may carry, the
						resolver says so <em>inside</em> the text above rather than trimming it
						silently — so a cut is visible here rather than inferred.
					</p>
				</>
			)}
			<p>
				<DiscussLink ref={answer.ref} label="Discuss this record" />
			</p>
		</div>
	);
}

/* ------------------------------------------------------------- the Knowledge */

export function KnowledgeView() {
	const [targetKey, setTargetKey] = useState('aas-forge');
	const [loaded, setLoaded] = useState<string | null>(null);
	const theory = useMemo(
		() =>
			createLoadable<TheoryPayload>(
				() => loaded === null
					? Promise.resolve({ ok: true as const, data: null as never })
					: readGateway<TheoryPayload>(
							`/api/scout/gateway/targets/${encodeURIComponent(loaded)}/theory`,
						),
				expectsMembers('found'),
			),
		[loaded],
	);
	const [hypothesesOlder, setHypothesesOlder] = useState<Hypothesis[]>([]);
	const [hypothesesMore, setHypothesesMore] = useState(false);
	const [hypothesesPaging, setHypothesesPaging] = useState(false);
	const [hypothesesPageError, setHypothesesPageError] = useState<GatewayError | null>(null);
	const falsified = useMemo(
		() =>
			createLoadable<{ hypotheses: Hypothesis[]; hasMore?: boolean }>(
				() =>
					readGateway(
						`/api/scout/gateway/hypotheses?statuses=REFUTED,FALSIFIED,REJECTED,SUPERSEDED&limit=${PAGE.hypotheses}`,
					),
				expectsMembers('hypotheses'),
			),
		[],
	);
	const weekly = useMemo(
		() =>
			createLoadable<WeeklyPayload>(
				() => readGateway('/api/scout/gateway/learning/weekly'),
				expectsMembers('weekly'),
			),
		[],
	);
	/** SCOUT'S OWN KNOWLEDGE LIBRARY — the primary read of this view.
	 *
	 *  FIRST PAGE VIA THE LOADABLE, CONTINUATION APPENDED (H-035). The old
	 *  control raised the requested limit from 50 to 200 and stopped: a library
	 *  past two hundred entries had an unreachable remainder presented as the
	 *  whole shelf. The route has carried `offset`/`hasMore` all along; this
	 *  view now uses them, and the same page-local pattern as the notifications
	 *  feed — the first page replaces, paging adds. */
	const [libraryQuery, setLibraryQuery] = useState('');
	const [libraryOlder, setLibraryOlder] = useState<LibraryEntry[]>([]);
	const [libraryMore, setLibraryMore] = useState(false);
	const [libraryPaging, setLibraryPaging] = useState(false);
	const [libraryPageError, setLibraryPageError] = useState<GatewayError | null>(null);
	const library = useMemo(
		() =>
			createLoadable<LibraryPayload>(
				() =>
					readGateway(
						`/api/scout/gateway/knowledge/entries?limit=${PAGE.knowledge}${
							libraryQuery.trim().length === 0
								? ''
								: `&query=${encodeURIComponent(libraryQuery.trim())}`
						}`,
					),
				expectsMembers('entries'),
			),
		[libraryQuery],
	);

	useEffect(() => {
		if (loaded !== null) void theory.load();
	}, [theory, loaded]);
	useEffect(() => {
		// A FRESH FIRST PAGE INVALIDATES THE APPENDED TAIL: it was a page of the
		// previous read, and keeping it would show rows from two different reads
		// as one list.
		setHypothesesOlder([]);
		setHypothesesMore(false);
		setLibraryOlder([]);
		setLibraryMore(false);
		void falsified.load();
		void weekly.load();
		void library.load();
	}, [falsified, weekly, library]);

	const theoryState = theory.state.value;
	const falsifiedState = falsified.state.value;
	const weeklyState = weekly.state.value;
	const libraryState = library.state.value;
	// A KNOWLEDGE CARD IS A FRAGMENT TARGET. Scout's prose links
	// `know:<id>` to `/discoveries/knowledge#<id>`; after the first page lands,
	// scroll to that card. This is best-effort across paging: an entry beyond
	// the first page is found by the Owner's own search, not silently pretended
	// to be on screen.
	useEffect(() => {
		if (libraryState.status !== 'ready') return;
		const raw = window.location.hash.replace(/^#/, '');
		if (raw.length === 0) return;
		let id = raw;
		try {
			id = decodeURIComponent(raw);
		} catch {
			// A malformed fragment is left as written; getElementById simply misses.
		}
		document.getElementById(id)?.scrollIntoView({ block: 'start' });
	}, [libraryState]);
	const falsifiedRows = [
		...(falsifiedState.data?.hypotheses ?? []),
		...hypothesesOlder,
	];
	const falsifiedHasMore =
		hypothesesMore ||
		(hypothesesOlder.length === 0 && falsifiedState.data?.hasMore === true);
	const libraryRows = [...(libraryState.data?.entries ?? []), ...libraryOlder];
	const libraryHasMore =
		libraryMore || (libraryOlder.length === 0 && libraryState.data?.hasMore === true);

	const loadMoreHypotheses = useCallback(async () => {
		if (falsifiedState.status !== 'ready' || falsifiedState.data === null) return;
		setHypothesesPaging(true);
		setHypothesesPageError(null);
		const offset = falsifiedState.data.hypotheses.length + hypothesesOlder.length;
		const result = await readGateway<{ hypotheses: Hypothesis[]; hasMore?: boolean }>(
			`/api/scout/gateway/hypotheses?statuses=REFUTED,FALSIFIED,REJECTED,SUPERSEDED&limit=${PAGE.hypotheses}&offset=${offset}`,
		);
		setHypothesesPaging(false);
		if (!result.ok) {
			setHypothesesPageError(result.error);
			return;
		}
		setHypothesesOlder((previous) => [...previous, ...result.data.hypotheses]);
		setHypothesesMore(result.data.hasMore === true);
	}, [falsifiedState, hypothesesOlder]);

	const loadMoreLibrary = useCallback(async () => {
		if (libraryState.status !== 'ready' || libraryState.data === null) return;
		setLibraryPaging(true);
		setLibraryPageError(null);
		const offset = libraryState.data.entries.length + libraryOlder.length;
		const result = await readGateway<LibraryPayload>(
			`/api/scout/gateway/knowledge/entries?limit=${PAGE.knowledge}&offset=${offset}`,
		);
		setLibraryPaging(false);
		if (!result.ok) {
			setLibraryPageError(result.error);
			return;
		}
		setLibraryOlder((previous) => [...previous, ...result.data.entries]);
		setLibraryMore(result.data.hasMore === true);
	}, [libraryState, libraryOlder]);

	return (
		<section class="view" aria-labelledby="knowledge-heading">
			<PageHeader
				id="knowledge-heading"
				title="Knowledge"
				about={{
					purpose:
						'What Scout currently holds, what it has abandoned, and what it knows it does not know.',
					how: (
						<p>
							Read a target’s current theory, the hypotheses the evidence went against, and what
							changed in the last seven days. Every list is a page; the counts it shows are the
							counts of that page, not of the corpus.
						</p>
					),
					limits: (
						<>
							<p>
								<strong>There is no per-record revision history.</strong> Belief revisions live in a
								store table and surface only inside a week’s window; hypothesis revisions are
								computed and then discarded, never persisted.
							</p>
							<p>
								<strong>Nothing here can be edited.</strong> The governed authority for changing
								what Scout holds does not exist in this product; inspection, traceability and
								discussion are the whole of it.
							</p>
							<p>
								<strong>Every list here is a page.</strong> A full page is followed by its own
								Load more control; the counts on each list describe the page on screen, not the
								corpus.
							</p>
						</>
					),
				}}
			/>

			{/* ------------------------------------------------ WHAT SCOUT LEARNED
			    THE LIBRARY COMES FIRST. "13 new questions, 1 weakness, 1
			    improvement" is activity metadata; it is not knowledge. This
			    section reads the entries themselves: the claim, why it matters,
			    what the evidence does not establish, its status and its sources.
			    The target theory and the weekly changes below are supporting
			    records, not a substitute for this reading. */}
			<section class="panel" aria-labelledby="learning-heading">
				<h2 id="learning-heading">What Scout has learned</h2>
				<p class="muted small">
					Each entry is written by Scout and linked to the evidence that supports it. The status is
					scientific, not decorative: an entry is SUPPORTED only when primary evidence was cited;
					a worker report cannot promote a claim. Stored knowledge is not canonical truth — read
					the status and the limits with the claim.
				</p>
				<label style="display:block; margin:8px 0;">
					<span class="small muted">
						Search the library (title, learned claim, limits; published space knowledge is
						included)
					</span>
					<input
						type="search"
						value={libraryQuery}
						placeholder="e.g. WAL checkpoint"
						onInput={(event) => setLibraryQuery((event.target as HTMLInputElement).value)}
						style="display:block; width:100%;"
					/>
				</label>
				{libraryState.status === 'loading' && (
					<p class="notice" role="status">
						Reading Scout’s knowledge library…
					</p>
				)}
				{libraryState.status === 'failed' && libraryState.error !== null && (
					<Failure
						error={libraryState.error}
						what="the knowledge library"
						onRetry={() => void library.load()}
					/>
				)}
				{libraryState.status === 'ready' && libraryState.data !== null && (
					<>
						{!libraryState.data.wired ? (
							<p class="notice">
								<strong>This deployment records no knowledge library.</strong>{' '}
								{libraryState.data.reason ??
									'That is a deployment gap, not evidence that Scout has learned nothing.'}
							</p>
						) : libraryRows.length === 0 ? (
							<p>
								<strong>No knowledge entry is on record yet.</strong>{' '}
								<span class="muted">
									Scout writes an entry only when research establishes something it can explain
									and cite; an empty library is preferable to five fabricated learnings. Open
									questions and failed approaches belong in the journal, not here.
								</span>
							</p>
						) : (
							<>
								<Bounded shown={libraryRows.length} noun="knowledge entry(ies)" />
								{/* THE INDEX IS FOR SCANNING; the entries below keep the full
								    argument and its citations. Selecting an index row is an
								    anchor to the entry, not a second route. */}
								<div class="table-wrap">
									<table class="data-table knowledge-index">
										<thead>
											<tr>
												<th scope="col">Knowledge</th>
												<th scope="col">Status</th>
												<th scope="col" class="numeric">Revision</th>
												<th scope="col">Recorded</th>
											</tr>
										</thead>
										<tbody>
											{libraryRows.map((entry) => (
												<tr key={`index:${entry.entryId}`} class="row-link">
													<td class="cell-title">
														<a href={`#${entry.entryId}`}>{entry.title}</a>
														<span class="cell-sub">{entry.whyItMatters}</span>
													</td>
													<td data-label="Status">
														<span class={`status status-${entry.knowledgeStatus.toLowerCase()}`}>
															{entry.knowledgeStatus}
														</span>
													</td>
													<td class="numeric" data-label="Revision">
														{entry.revision}
													</td>
													<td class="cell-date" data-label="Recorded">
														{formatRecordTime(entry.createdAt)}
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
								<ul class="records library">
									{libraryRows.map((entry) => (
										<li key={entry.entryId} id={entry.entryId} class="record knowledge-entry">
											<div class="record-head">
												<h3 class="record-title">{entry.title}</h3>
												<span
													class={`tag${
														entry.knowledgeStatus === 'SUPPORTED'
															? ' tag-live'
															: entry.knowledgeStatus === 'PROVISIONAL' ||
																  entry.knowledgeStatus === 'UNRESOLVED' ||
																  entry.knowledgeStatus === 'DISPUTED' ||
																  entry.knowledgeStatus === 'REFUTED'
																? ' tag-warn'
																: ''
													}`}
												>
													{entry.knowledgeStatus}
												</span>
												{entry.revision > 1 && <span class="tag">revision {entry.revision}</span>}
											</div>
											<Markdown
												content={entry.learned}
												class="turn-markdown authored-content"
											/>
											<p>
												<strong>Why it matters.</strong> {entry.whyItMatters}
											</p>
											<p>
												<strong>What the evidence does not establish.</strong>{' '}
												{entry.limitations}
											</p>
											{entry.statusNote !== null && (
												<p class="notice small" role="status">
													{entry.statusNote}
												</p>
											)}
											<p class="muted small">
												{entry.topicId !== null && (
													<>
														<a href={`/research/topic/${encodeURIComponent(entry.topicId)}`}>
															Research topic
														</a>{' '}
														<code>{entry.topicId}</code> ·{' '}
													</>
												)}
												Scout wrote this{' '}
												<time dateTime={entry.createdAt}>
													{formatRecordTime(entry.createdAt)}
												</time>
												{entry.episodeId !== null && (
													<>
														{' '}
														· from investigation <code>{entry.episodeId}</code>
													</>
												)}
												{entry.runId !== null && (
													<>
														{' '}
														· in window <code>{entry.runId}</code>
													</>
												)}
												{entry.supersedesEntryId !== null && (
													<>
														{' '}
														· supersedes <code>{entry.supersedesEntryId}</code>
													</>
												)}
												{entry.supersededByEntryId !== null && (
													<>
														{' '}
														· superseded by <code>{entry.supersededByEntryId}</code>
													</>
												)}
											</p>
											{entry.evidenceRefs.length > 0 && (
												<Technical summary={`${entry.evidenceRefs.length} primary evidence reference(s)`}>
													<ul class="references">
														{entry.evidenceRefs.map((ref) => (
															<li key={ref}>
																<code>{ref}</code>
															</li>
														))}
													</ul>
												</Technical>
											)}
											{entry.workerReportRefs.length > 0 && (
												<Technical summary={`${entry.workerReportRefs.length} worker report(s) — not primary evidence`}>
													<ul class="references">
														{entry.workerReportRefs.map((ref) => (
															<li key={ref}>
																<code>{ref}</code>
															</li>
														))}
													</ul>
												</Technical>
											)}
										</li>
									))}
								</ul>
								{libraryHasMore && (
									<button
										type="button"
										class="button ghost"
										disabled={libraryPaging}
										onClick={() => void loadMoreLibrary()}
									>
										{libraryPaging ? 'Reading the next page…' : 'Load more knowledge'}
									</button>
								)}
								{libraryPageError !== null && (
									<p class="notice small" role="status">
										The next page of knowledge could not be read: {libraryPageError.message}
									</p>
								)}
							</>
						)}
					</>
				)}
			</section>

			<section class="panel" aria-labelledby="theory-heading">
				<h2 id="theory-heading">What Scout holds about a target</h2>
				<form
					class="inline-form"
					onSubmit={(event: Event) => {
						event.preventDefault();
						setLoaded(targetKey.trim().length === 0 ? null : targetKey.trim());
					}}
				>
					<input
						type="text"
						class="composer-input"
						aria-label="A target key"
						value={targetKey}
						onInput={(event: Event) => setTargetKey((event.target as HTMLInputElement).value)}
					/>{' '}
					<button type="submit" class="button">
						Read
					</button>
				</form>
				{theoryState.status === 'loading' && (
					<p class="notice" role="status">
						Reading…
					</p>
				)}
				{theoryState.status === 'failed' && theoryState.error !== null && (
					<Failure error={theoryState.error} what="that target’s theory" onRetry={() => void theory.load()} />
				)}
				{theoryState.status === 'ready' && theoryState.data !== null && (
					<TheoryBody payload={theoryState.data} />
				)}
			</section>

			<section class="panel" aria-labelledby="falsified-heading">
				<h2 id="falsified-heading">Hypotheses the evidence went against</h2>
				<p class="muted small">
					Asked for by STATUS, on the server — which is a fix rather than a preference. The filter
					existed in the service and the controller passed nothing, so this question could only have
					been answered by listing every hypothesis and filtering in the browser: a filter over an
					arbitrary fifty, presented as a filter over the corpus.
				</p>
				{falsifiedState.status === 'loading' && (
					<p class="notice" role="status">
						Reading hypotheses…
					</p>
				)}
				{falsifiedState.status === 'failed' && falsifiedState.error !== null && (
					<Failure
						error={falsifiedState.error}
						what="the hypotheses"
						onRetry={() => void falsified.load()}
					/>
				)}
				{falsifiedState.status === 'ready' && falsifiedState.data !== null && (
					<>
						{falsifiedRows.length === 0 ? (
							<p>
								<strong>No hypothesis is recorded as having been gone against.</strong>{' '}
								<span class="muted">
									That is a statement about what has been TESTED and recorded, not a claim that
									everything Scout holds is right.
								</span>
							</p>
						) : (
							<>
								<Bounded shown={falsifiedRows.length} noun="hypothesis(es)" />
								<ul class="records">
									{falsifiedRows.map((hypothesis) => (
										<li key={hypothesis.hypothesisId} class="record">
											<div class="record-head">
												<code>{hypothesis.hypothesisId}</code>
												<span class="tag tag-warn">{hypothesis.status}</span>
											</div>
											<p class="record-summary">{hypothesis.statement}</p>
											<p class="muted small">
												confidence {hypothesis.confidence.toLowerCase()}
												{hypothesis.supersededByHypothesisId !== null && (
													<>
														{' '}
														· superseded by <code>{hypothesis.supersededByHypothesisId}</code>
													</>
												)}
											</p>
											{hypothesis.convergenceReason !== null && (
												<p class="small">{hypothesis.convergenceReason}</p>
											)}
											<p>
												<DiscussLink
													ref={`hypothesis:${hypothesis.hypothesisId}`}
													label="Discuss"
												/>
											</p>
										</li>
									))}
								</ul>
								{falsifiedHasMore && (
									<button
										type="button"
										class="button ghost"
										disabled={hypothesesPaging}
										onClick={() => void loadMoreHypotheses()}
									>
										{hypothesesPaging ? 'Reading the next page…' : 'Load more hypotheses'}
									</button>
								)}
								{hypothesesPageError !== null && (
									<p class="notice small" role="status">
										The next page of hypotheses could not be read: {hypothesesPageError.message}
									</p>
								)}
							</>
						)}
					</>
				)}
			</section>

			<section class="panel" aria-labelledby="revisions-heading">
				<h2 id="revisions-heading">Recent changes — activity metadata, not knowledge</h2>
				<p class="muted small">
					This is a seven-day count of belief revisions and falsifications. It is useful context for
					the library above, and it is not itself a learning: a revision count does not tell you
					what was learned.
				</p>
				{weeklyState.status === 'loading' && (
					<p class="notice" role="status">
						Reading the week…
					</p>
				)}
				{weeklyState.status === 'failed' && weeklyState.error !== null && (
					<Failure error={weeklyState.error} what="the week’s learning" onRetry={() => void weekly.load()} />
				)}
				{weeklyState.status === 'ready' && weeklyState.data !== null && (
					<>
						{!weeklyState.data.weekly.material ? (
							<p>
								<strong>{weeklyState.data.weekly.summary}</strong>{' '}
								<span class="muted">
									Nothing material moved in the window{' '}
									<time dateTime={weeklyState.data.weekly.windowStart}>
										{weeklyState.data.weekly.windowStart}
									</time>{' '}
									to{' '}
									<time dateTime={weeklyState.data.weekly.windowEnd}>
										{weeklyState.data.weekly.windowEnd}
									</time>
									. That is the record’s own word for it, not this screen’s.
								</span>
							</p>
						) : (
							<>
								<p>{weeklyState.data.weekly.summary}</p>
								{(weeklyState.data.weekly.revisions ?? []).length > 0 && (
									<ul class="records">
										{(weeklyState.data.weekly.revisions ?? []).map((revision) => (
											<li key={`${revision.beliefKey}:${revision.changedAt}`} class="record">
												<div class="record-head">
													<code>{revision.beliefKey}</code>
													<span class="tag">{revision.changeKind}</span>
												</div>
												<p class="small">{revision.reason}</p>
												<p class="muted small">
													<time dateTime={revision.changedAt}>{revision.changedAt}</time>
												</p>
											</li>
										))}
									</ul>
								)}
							</>
						)}
						<p class="muted small">
							Revisions are readable here only inside a seven-day window. The product has no route
							that lists a belief’s own history, so this is the only place they surface.
						</p>
					</>
				)}
			</section>
		</section>
	);
}

function TheoryBody({ payload }: { payload: TheoryPayload }) {
	if (!payload.found || payload.theory === undefined) {
		return (
			<p class="notice">
				<strong>No theory is on record for this target.</strong>{' '}
				<span class="muted">
					Scout builds a theory as it investigates; a target it has never looked into has none. That is
					different from a theory that is empty, and this read cannot tell you which you are seeing —
					it says only that no row answered.
				</span>
			</p>
		);
	}
	const beliefs = payload.beliefs ?? [];
	const unknowns = payload.knownUnknowns ?? [];
	return (
		<>
			<p class="muted small">
				revision {payload.theory.revision} · updated{' '}
				<time dateTime={payload.theory.updatedAt}>{payload.theory.updatedAt}</time>
			</p>
			<h3 class="subhead">What it holds ({beliefs.length})</h3>
			{beliefs.length === 0 ? (
				<p class="muted small">No belief is on record for this target.</p>
			) : (
				<ul class="records">
					{beliefs.map((belief) => (
						<li key={belief.beliefKey} class="record">
							<div class="record-head">
								<code>{belief.beliefKey}</code>
								<span class={`tag${belief.status === 'FALSIFIED' ? ' tag-warn' : ''}`}>
									{belief.status}
								</span>
							</div>
							<p class="record-summary">{belief.statement}</p>
							<p class="muted small">
								confidence {belief.confidence.toLowerCase()}
								{belief.supersededByBeliefKey !== null && (
									<>
										{' '}
										· superseded by <code>{belief.supersededByBeliefKey}</code>
									</>
								)}
								{belief.lastRevisedAt !== null && (
									<>
										{' '}
										· last revised{' '}
										<time dateTime={belief.lastRevisedAt}>{belief.lastRevisedAt}</time>
									</>
								)}
							</p>
						</li>
					))}
				</ul>
			)}

			{/* WHAT SCOUT KNOWS IT DOES NOT KNOW. Rendered as its own section rather
			    than as a footnote, because an open question is a result. */}
			<h3 class="subhead">What it knows it does not know ({unknowns.length})</h3>
			{unknowns.length === 0 ? (
				<p class="muted small">
					No open unknown is recorded. The record keeps resolved ones too and this read returns both,
					so an empty list here means none was ever recorded rather than none is open.
				</p>
			) : (
				<ul class="records">
					{unknowns.map((unknown) => (
						<li key={unknown.unknownKey} class="record">
							<div class="record-head">
								<code>{unknown.unknownKey}</code>
								<span class={`tag${unknown.resolvedAt === null ? ' tag-warn' : ''}`}>
									{unknown.resolvedAt === null ? 'OPEN' : 'resolved'}
								</span>
							</div>
							<p class="record-summary">{unknown.statement}</p>
							<p class="muted small">
								recorded <time dateTime={unknown.recordedAt}>{unknown.recordedAt}</time>
								{unknown.resolvedAt !== null && (
									<>
										{' '}
										· resolved <time dateTime={unknown.resolvedAt}>{unknown.resolvedAt}</time>
									</>
								)}
							</p>
						</li>
					))}
				</ul>
			)}
		</>
	);
}
